import { parseFullSymbol } from "./helpers.js";
import { subscribeOnStream, unsubscribeFromStream } from "./streaming.js";
import {
  DEFAULT_CURRENCIES,
  DEFAULT_MARKETS,
} from "@core/utils/consts";
import ohlcv from "@api/ohlcv.js";
import { getExchangeSlug, getBrandDisplayName } from "@core/utils";
import { getMarketsCatalogSnapshot } from "@core/utils/marketsCatalogStore";
import { getQuoteLabel } from "@core/utils/getCurrencyMeta";

const lastBarsCache = new Map();

const exchangeSlug = getExchangeSlug();
const brandName = getBrandDisplayName();

const configurationData = {
  // Resolutions below 60 minutes are disabled by product policy.
  // Must match Dena/Peatio k-line periods (same set as Gereh).
  supported_resolutions: ["60", "120", "240", "360", "720", "1D"],
  exchanges: [
    {
      value: exchangeSlug,
      name: brandName,
      desc: brandName,
    },
  ],
  symbols_types: [
    {
      name: "crypto",

      // `symbolType` argument for the `searchSymbols` method, if a user selects this symbol type
      value: "crypto",
    },
  ],
};

async function getAllSymbols() {
  const catalog = getMarketsCatalogSnapshot();
  if (catalog?.markets?.length) {
    return catalog.markets.map((m) => ({
      description: `${m.base} - ${getQuoteLabel(m.quote)}`,
      exchange: exchangeSlug,
      full_name: `${exchangeSlug}:${m.symbol}`,
      symbol: m.symbol,
      ticker: m.symbol,
      type: "crypto",
    }));
  }

  let allSymbols = [];

  allSymbols = DEFAULT_CURRENCIES.map((currency) =>
    Object.entries(currency.SYMBOLS).map(([toSym, symbol]) =>
      symbol.length > 0
        ? {
            description: currency.NAME + " - " + DEFAULT_MARKETS.t[toSym],
            exchange: exchangeSlug,
            full_name: `${exchangeSlug}:${symbol}`,
            symbol: symbol,
            ticker: symbol,
            type: "crypto",
          }
        : null
    )
  )
    .flat()
    .filter((item) => item);
  // console.log("matched", allSymbols);
  return allSymbols;
}

export default {
  onReady: (callback) => {
    // console.log("[onReady]: Method call");
    setTimeout(() => callback(configurationData));
  },

  searchSymbols: async (
    userInput,
    exchange,
    symbolType,
    onResultReadyCallback
  ) => {
    // console.log("[searchSymbols]: Method call");
    const symbols = await getAllSymbols();
    // console.log("[searchSymbols]: Method call", symbols);
    const newSymbols = symbols.filter((symbol) => {
      const isExchangeValid = exchange === "" || symbol.exchange === exchange;
      const isFullSymbolContainsInput =
        symbol.full_name.toLowerCase().indexOf(userInput.toLowerCase()) !== -1;
      return isExchangeValid && isFullSymbolContainsInput;
    });
    // console.log("[searchSymbols]: newSymbols", newSymbols);

    onResultReadyCallback(newSymbols);
  },

  resolveSymbol: async (
    symbolName,
    onSymbolResolvedCallback,
    onResolveErrorCallback
  ) => {
    // console.log("[resolveSymbol]: Method call", symbolName);
    const symbols = await getAllSymbols();
    // console.log("[resolveSymbol]: Method call: symbols", symbols);
    const symbolItem = symbols.find(
      ({ full_name, symbol }) =>
        full_name === symbolName || symbol === symbolName
    );
    if (!symbolItem) {
      // console.log("[resolveSymbol]: Cannot resolve symbol", symbolName);
      onResolveErrorCallback("cannot resolve symbol");
      return;
    }

    const parsedSymbol = parseFullSymbol(symbolItem.full_name);
    const catalog = getMarketsCatalogSnapshot();
    const catalogMarket = catalog?.markets?.find(
      (m) =>
        m.symbol === symbolItem.symbol ||
        m.marketKey ===
          `${parsedSymbol?.fromSymbol}_${parsedSymbol?.toSymbol}`
    );
    const pricePrecision = Number.isFinite(catalogMarket?.pricePrecision)
      ? catalogMarket.pricePrecision
      : parsedSymbol?.toSymbol === DEFAULT_MARKETS.IRT
        ? 0
        : 2;

    const symbolInfo = {
      name: symbolItem.symbol,
      description: symbolItem.description,
      type: symbolItem.type,
      session: "24x7",
      // Exchange timezone: Iran permanent UTC+03:30
      timezone: "Asia/Tehran",
      exchange: symbolItem.exchange,
      full_name: symbolItem.full_name,
      ticker: symbolItem.ticker || symbolItem.symbol,
      minmov: 1,
      pricescale: Math.pow(10, pricePrecision),
      has_intraday: true,
      intraday_multipliers: configurationData.supported_resolutions,
      has_no_volume: false,
      has_weekly_and_monthly: false,
      supported_resolutions: configurationData.supported_resolutions,
      volume_precision: 8,
      data_status: "streaming",
    };
    onSymbolResolvedCallback(symbolInfo);
  },

  getBars: async (
    symbolInfo,
    resolution,
    from,
    to,
    onHistoryCallback,
    onErrorCallback,
    firstDataRequest
  ) => {
    // Prefer explicit ticker/full_name; fall back to name (e.g. BTCIRT).
    const fullName =
      symbolInfo.full_name ||
      (symbolInfo.exchange && symbolInfo.name
        ? `${symbolInfo.exchange}:${symbolInfo.name}`
        : symbolInfo.name);
    const parsedSymbol = parseFullSymbol(fullName);
    if (!parsedSymbol?.fromSymbol || !parsedSymbol?.toSymbol) {
      onHistoryCallback([], { noData: true });
      return;
    }

    const urlParameters = {
      market: `${parsedSymbol.fromSymbol}_${parsedSymbol.toSymbol}`,
      resolution,
      from,
      to,
    };

    try {
      const data = await ohlcv.getOhlcvTV(urlParameters);
      const raw = Array.isArray(data?.content) ? data.content : [];

      // Keep ascending order (same as Gereh). Do NOT reverse — TV requires oldest→newest.
      const bars = raw
        .filter((bar) => {
          const t = Number(bar.time);
          return Number.isFinite(t) && t >= from && t < to;
        })
        .map((bar) => ({
          time: Number(bar.time) * 1000,
          low: bar.low,
          high: bar.high,
          open: bar.open,
          close: bar.close,
          volume: bar.volumeFrom,
        }));

      if (bars.length === 0) {
        onHistoryCallback([], { noData: true });
        return;
      }

      if (firstDataRequest) {
        lastBarsCache.set(fullName, { ...bars[bars.length - 1] });
      }
      onHistoryCallback(bars, { noData: false });
    } catch (error) {
      onErrorCallback(error);
    }
  },

  subscribeBars: (
    symbolInfo,
    resolution,
    onRealtimeCallback,
    subscribeUID,
    onResetCacheNeededCallback
  ) => {
    subscribeOnStream(
      symbolInfo,
      resolution,
      onRealtimeCallback,
      subscribeUID,
      onResetCacheNeededCallback,
      lastBarsCache.get(symbolInfo.full_name)
    );
  },

  unsubscribeBars: (subscriberUID) => {
    unsubscribeFromStream(subscriberUID);
  },
};
